import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Home,
  MessageSquare,
  FolderOpen,
  Briefcase,
  CalendarDays,
  Mic,
  Settings,
  Focus,
  Search,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PORTAL_SHELL } from './portalTheme';
import { kanzleiChatNavVisible } from './elementSeatInvite';
import {
  isDemoNavHiddenView,
  PORTAL_NAV_MATRIX_LABEL,
  type PortalView,
} from './portalNavigation';

export interface PortalCommandPaletteItem {
  id: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  keywords?: string;
  run: () => void;
}

export interface PortalCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ohne Element am eigenen Sitz kein Eintrag Kanzlei-Chat. */
  elementSeat?: boolean;
  onNavigate: (view: PortalView) => void;
  onOpenSettings: () => void;
  onFocusArko?: () => void;
}

function normalizeQuery(q: string): string {
  return q.trim().toLowerCase();
}

export const PortalCommandPalette: React.FC<PortalCommandPaletteProps> = ({
  open,
  onOpenChange,
  elementSeat = false,
  onNavigate,
  onOpenSettings,
  onFocusArko,
}) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const close = useCallback(() => {
    onOpenChange(false);
    setQuery('');
    setActiveIndex(0);
  }, [onOpenChange]);

  const commands = useMemo<PortalCommandPaletteItem[]>(() => {
    const go = (view: PortalView) => {
      close();
      onNavigate(view);
    };
    const items: PortalCommandPaletteItem[] = [
      {
        id: 'home',
        label: 'Start',
        hint: 'Übersicht',
        icon: Home,
        keywords: 'start home übersicht',
        run: () => go('home'),
      },
      {
        id: 'chat',
        label: 'ARKO Chat',
        hint: 'Kanzlei-Assistent',
        icon: MessageSquare,
        keywords: 'arko chat assistent',
        run: () => go('chat'),
      },
      {
        id: 'suche',
        label: 'Suche',
        hint: '§ / Normen und OGH / Judikatur',
        icon: Search,
        keywords: 'suche paragraph lexcore ogh judikatur norm normen',
        run: () => go('suche'),
      },
      {
        id: 'dokumente',
        label: 'Dokumente',
        hint: 'Dateien & Ablage',
        icon: FolderOpen,
        keywords: 'dokumente dateien protokolle upload',
        run: () => go('dokumente'),
      },
      {
        id: 'akten',
        label: 'Akten',
        hint: 'Anlegen und in ARKO öffnen',
        icon: Briefcase,
        keywords: 'akten akte fall mandat',
        run: () => go('akten'),
      },
      {
        id: 'kalender',
        label: 'Kalender',
        hint: 'Monat, Termine, Fristen',
        icon: CalendarDays,
        keywords: 'kalender termin frist monat beratung',
        run: () => go('kalender'),
      },
      {
        id: 'diktat',
        label: 'Diktat',
        hint: 'Audio hochladen',
        icon: Mic,
        keywords: 'diktat audio sprache',
        run: () => go('diktat'),
      },
      {
        id: 'matrix',
        label: PORTAL_NAV_MATRIX_LABEL,
        hint: 'Kanzlei-Raum (Element)',
        icon: Users,
        keywords: 'matrix element raum diktat chat',
        run: () => go('matrix'),
      },
      {
        id: 'settings',
        label: 'Einstellungen',
        hint: 'Passwort & Passkeys',
        icon: Settings,
        keywords: 'einstellungen passwort passkey',
        run: () => {
          close();
          onOpenSettings();
        },
      },
    ];
    if (onFocusArko) {
      items.push({
        id: 'focus-arko',
        label: 'ARKO fokussieren',
        hint: 'Eingabefeld · /',
        icon: Focus,
        keywords: 'fokus eingabe slash',
        run: () => {
          close();
          onFocusArko();
        },
      });
    }
    // Vertragsprüfung bleibt im View-Code, öffnet in der Demo weder Mehr noch Palette.
    return items.filter((item) => {
      if (item.id === 'matrix' && !kanzleiChatNavVisible(elementSeat)) return false;
      return !isDemoNavHiddenView(item.id);
    });
  }, [close, elementSeat, onNavigate, onOpenSettings, onFocusArko]);

  const filtered = useMemo(() => {
    const q = normalizeQuery(query);
    if (!q) return commands;
    return commands.filter((c) => {
      const hay = `${c.label} ${c.hint ?? ''} ${c.keywords ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [commands, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
        return;
      }
      if (filtered.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % filtered.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        filtered[activeIndex]?.run();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, close, filtered, activeIndex]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-cmd-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  if (!open) return null;

  return (
    <div className={PORTAL_SHELL.commandPaletteOverlay} role="presentation">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Schnellzugriff schließen"
        onClick={close}
      />
      <div
        className={`${PORTAL_SHELL.commandPalettePanel} relative z-[1]`}
        role="dialog"
        aria-modal="true"
        aria-label="Schnellzugriff"
      >
        <div className="flex items-center gap-2 border-b border-white/[0.06] px-3 py-2.5">
          <Search className="w-4 h-4 text-slate-500 flex-shrink-0" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Bereich oder Aktion suchen…"
            className={`${PORTAL_SHELL.input} border-0 bg-transparent ring-0 focus:ring-0`}
            aria-controls="portal-cmdk-list"
            aria-activedescendant={
              filtered.length > 0 ? `portal-cmdk-item-${activeIndex}` : undefined
            }
          />
          <kbd className="hidden sm:inline text-[10px] text-slate-600 border border-white/[0.08] rounded px-1.5 py-0.5">
            Esc
          </kbd>
        </div>
        <ul
          id="portal-cmdk-list"
          ref={listRef}
          className="max-h-[min(50vh,320px)] overflow-y-auto py-1"
          role="listbox"
        >
          {filtered.length === 0 ? (
            <li className="px-4 py-6 text-center text-sm text-slate-500">Kein Treffer</li>
          ) : (
            filtered.map((item, index) => {
              const Icon = item.icon;
              const selected = index === activeIndex;
              return (
                <li key={item.id} role="presentation">
                  <button
                    id={`portal-cmdk-item-${index}`}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    data-cmd-index={index}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => item.run()}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                      selected ? 'bg-white/[0.06] text-white' : 'text-slate-300 hover:bg-white/[0.04]'
                    }`}
                  >
                    <Icon className="w-4 h-4 text-slate-500 flex-shrink-0" aria-hidden />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium truncate">{item.label}</span>
                      {item.hint ? (
                        <span className="block text-[11px] text-slate-500 truncate">{item.hint}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
};
